import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { PaymentsService } from '../../../services/payment.service';
import { AgendaEvent, ClientPaymentHistory } from '../../../shared/interfaces';
import { PaymentStatusEsPipe } from '../../../shared/pipes/paymentStatusEs.pipe';
import { parseDbUtc } from '../../../shared/common/utils';

type PaymentRow = ClientPaymentHistory['payments'][number] & {
  serviceName: string | null;
  eventDate: Date | null;
  pending: number;
  isAppointment: boolean;
};

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * Payments of a client: what is still owed and the most recent charges,
 * labelled with the service of the appointment they belong to.
 */
@Component({
  selector: 'app-client-payment-history',
  standalone: true,
  imports: [CommonModule, PaymentStatusEsPipe],
  templateUrl: './client-payment-history.html',
  styleUrls: ['../people-profile/people-shared.css', './client-payment-history.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientPaymentHistoryComponent implements OnChanges {
  @Input() clientId: string | null = null;
  /** Client appointments, used to name each payment after its service. */
  @Input() set events(value: AgendaEvent[] | null) {
    this.eventsSignal.set(value ?? []);
  }

  private paymentsService = inject(PaymentsService);
  private eventsSignal = signal<AgendaEvent[]>([]);
  private static readonly COLLAPSED_COUNT = 5;

  history = signal<ClientPaymentHistory | null>(null);
  isLoading = signal(false);
  loadFailed = signal(false);
  expanded = signal(false);

  rows = computed<PaymentRow[]>(() => {
    const byId = new Map(this.eventsSignal().map((e) => [e.eventId, e]));
    return (this.history()?.payments ?? []).map((p) => {
      const isAppointment = !!p.eventId && p.eventId !== EMPTY_GUID;
      const event = isAppointment ? byId.get(p.eventId) : undefined;
      return {
        ...p,
        isAppointment,
        serviceName: event?.serviceName ?? null,
        eventDate: event ? parseDbUtc(event.startDate) : null,
        pending: p.status === 'paid' ? 0 : Math.max(p.amount - p.paidAmount, 0),
      };
    });
  });
  visibleRows = computed(() =>
    this.expanded() ? this.rows() : this.rows().slice(0, ClientPaymentHistoryComponent.COLLAPSED_COUNT)
  );
  totalPending = computed(() => this.rows().reduce((sum, r) => sum + r.pending, 0));
  totalPaid = computed(() => this.rows().reduce((sum, r) => sum + r.paidAmount, 0));
  hiddenCount = computed(() => this.rows().length - ClientPaymentHistoryComponent.COLLAPSED_COUNT);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] && this.clientId) {
      this.load();
    }
  }

  load(): void {
    if (!this.clientId) return;
    this.isLoading.set(true);
    this.loadFailed.set(false);
    this.paymentsService.getClientPaymentHistory(this.clientId).subscribe({
      next: (data) => {
        this.history.set(data);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.loadFailed.set(true);
      },
    });
  }

  methodLabel(method: string): string {
    const labels: Record<string, string> = {
      epayco_link: 'Link de pago',
      manual: 'Manual',
      cash: 'Efectivo',
      card: 'Tarjeta',
      transfer: 'Transferencia',
      credit: 'Saldo a favor',
      client_credit: 'Saldo a favor',
      other: 'Otro',
    };
    return labels[method] || method || '';
  }

  toDate(value: string): Date {
    return parseDbUtc(value);
  }

  formatMoney(value: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(value);
  }
}
